import React, { useRef, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  useSpeechRecognition,
  isSpeechRecognitionSupported,
} from '../../hooks/useSpeechRecognition';

export type VoiceListenState = 'idle' | 'listening' | 'processing';

interface VoiceInputProps {
  state: VoiceListenState;
  onStateChange: (state: VoiceListenState) => void;
  onTranscript: (text: string) => void;
  disabled?: boolean;
  lang?: string;
}

const MicIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <rect x="9" y="1" width="6" height="11" rx="3" />
    <path
      d="M5 10a7 7 0 0 0 14 0"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
    <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="9" y1="21" x2="15" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const MicOffIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

export const isVoiceSupported = isSpeechRecognitionSupported;

const VoiceInput: React.FC<VoiceInputProps> = ({
  state,
  onStateChange,
  onTranscript,
  disabled = false,
  lang = 'fr-CA',
}) => {
  const { isDark } = useTheme();
  const stateRef = useRef<VoiceListenState>(state);

  // Keep stateRef in sync
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const { start, stop } = useSpeechRecognition({
    lang,
    onListeningStart: () => {
      stateRef.current = 'listening';
      onStateChange('listening');
    },
    onTranscript: (text) => {
      stateRef.current = 'idle';
      onStateChange('idle');
      onTranscript(text);
    },
    onEnd: () => {
      stateRef.current = 'idle';
      onStateChange('idle');
    },
  });

  const handleClick = () => {
    if (disabled || state === 'processing') return;
    if (stateRef.current === 'listening') {
      stop();
      stateRef.current = 'idle';
      onStateChange('idle');
    } else if (stateRef.current === 'idle') {
      start();
    }
  };

  if (!isVoiceSupported) return null;

  const btnCls =
    state === 'listening'
      ? 'bg-red-500 hover:bg-red-600 text-white shadow-red-500/25 animate-pulse'
      : state === 'processing'
      ? 'bg-indigo-500 text-white shadow-indigo-500/25 cursor-not-allowed'
      : isDark
      ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
      : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || state === 'processing'}
      title={state === 'listening' ? 'Stop listening' : 'Start voice input'}
      aria-label={state === 'listening' ? 'Stop listening' : 'Start voice input'}
      className={`p-3 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center disabled:cursor-not-allowed ${btnCls}`}
    >
      {state === 'processing' ? (
        <SpinnerIcon />
      ) : state === 'listening' ? (
        <MicOffIcon />
      ) : (
        <MicIcon />
      )}
    </button>
  );
};

export default VoiceInput;
